import { DefaultButton, FontIcon, Icon, Link, mergeStyles, PrimaryButton, Stack } from '@fluentui/react';
import * as React from 'react';
import { StyledText } from '../../../../web/common/components/StyledText';
import { SMCTicketing } from '../../../../web/models/constants/getHelpConstants';
import { getHelpLogger } from '../../../../web/utils/getHelpLogger';
import { getString } from '../../../../web/utils/stringLoader';
import { AutomationIds } from '../../../models/constants/automationIds';
import { M365UpsellNonAgentModalityId, C2CSkylightNonAgentModalityId, C2CSkylightOfficeNonAgentModalityId } from '../../../models/constants/constants';
import { isGetHelp, onOverrideClickBehavior, sanitizeHTML } from '../../../utilities';
import { NonAgentModality, NonAgentModalityType } from '../models/modalityModels';
import { FreelanceRequest, MetaDataKeys } from '../models/requestModels';
import { getLinkStyles } from '../styles/commonStyles';
import { getContactUsClassNames, getContactUsStyles } from '../styles/contactUsStyles';
import { getModalityIconStyle, getModalityLinkStyle, getModalitySectionBoxStyle, getUpSellDescriptionStyle, getUpsellButtonStyles, modalityDescriptionStyle, upSellTitleStyle } from '../styles/modalityStyles';
import { IContactUsCurrentTheme } from '../styles/theme.model';
import { IThemedProp } from '../styles/themesUI';
import { collectTelemetry } from '../../../../web/utils/oneDsUtil';
import { LogHint, LogType } from '../../../../web/models/emeraldLoggingRequest';

const setDangerousInnerHtml = (text: string) => {
    // eslint-disable-next-line react/no-danger
    return <div dangerouslySetInnerHTML={sanitizeHTML(text, ['target'])} />;
};

export interface NonAgentModalityOEMProps {
  nonAgentModality: NonAgentModality;
  theme: IContactUsCurrentTheme;
  alchemyTheme?: string;
}

export const NonAgentModalityOemBox = (props: NonAgentModalityOEMProps) => {
  if (props.nonAgentModality.Type !== NonAgentModalityType.OEM) {
    return null;
  }

  const onClick = async (event?: React.MouseEvent) => {
    collectTelemetry({
      ContentId: 'ClickedNonAgentModality', Type: LogType.PageAction, Hint: LogHint.ClickedNonAgentModality,
      Details: { 'modality': props.nonAgentModality.Id, 'theme': props.alchemyTheme, 'link': props.nonAgentModality.Link }
    });
    getHelpLogger.logNonAgentModalityClicked(props.nonAgentModality.Id, props.nonAgentModality.Link, props.alchemyTheme);
  };
  return (
    <Stack styles={getModalitySectionBoxStyle()} horizontal tokens={{ childrenGap: 23 }}>
      <Icon iconName="System" styles={getModalityIconStyle(props.theme.palette.themeDarkAlt)} />
      <Stack>
        <Link
          id={`NonAgent_Modality_${props.nonAgentModality.Id}`}
          styles={getModalityLinkStyle(props.theme.palette.themeDarkAlt)}
          href={props.nonAgentModality.Link}
          data-bi-bhvr='CONTACT'
          data-bi-area='modalitiesSection'
          data-bi-formnm='maxFunnel'
          data-bi-name={`NonAgent_Modality_${props.nonAgentModality.Id}`}
          target="_blank"
          onClick={onClick}
        >
          {props.nonAgentModality.Title}
        </Link>
        <StyledText styles={modalityDescriptionStyle}>
          {setDangerousInnerHtml(props.nonAgentModality.Description)}
        </StyledText>
      </Stack>
    </Stack>
  );
};

export const NonAgentModalityOEM = (props: NonAgentModalityOEMProps) => {
  const contactUsClassNames = getContactUsClassNames(getContactUsStyles, { theme: props.theme });
  const linkStyles = getLinkStyles();
  const icon = <FontIcon className={contactUsClassNames.requestModalityIcon} aria-label={getString('ModalitiesPageOEMTitle')} iconName="System" />;
  return (
    (props.nonAgentModality.Type === NonAgentModalityType.OEM) &&
    <div className={mergeStyles('col-lg-12', contactUsClassNames.col, contactUsClassNames.bodyCopySmall, contactUsClassNames.modalityOption, contactUsClassNames.wrapText)}>
      <Link className={contactUsClassNames.modalityOptionTitle}
        href={props.nonAgentModality.Link}
        id={`NonAgent_Modality_${props.nonAgentModality.Id}`}
        styles={linkStyles}
        data-bi-bhvr='CONTACT'
        data-bi-area='modalitiesSection'
        data-bi-formnm='maxFunnel'
        data-bi-name={`NonAgent_Modality_${props.nonAgentModality.Id}`}
        target='_blank'>
          {icon} {props.nonAgentModality.Title}
      </Link>
      <div className={contactUsClassNames.descriptionHtmlText}>{setDangerousInnerHtml(props.nonAgentModality.Description)}</div>
    </div>
  );
};

export interface NonAgentModalityStandardProps {
  nonAgentModality: NonAgentModality;
  theme: IContactUsCurrentTheme;
  isGetHelp?: boolean;
  isDarkTheme?: boolean;
  alchemyTheme?: string;
  articleOnClickHandler?: (url: string) => {};
}

export const NonAgentModalityStandardBox = (props: NonAgentModalityStandardProps) => {
  if (props.nonAgentModality.Type !== NonAgentModalityType.Standard) {
    return null;
  }

  const onClick = async (event?: React.MouseEvent) => {
    getHelpLogger.logNonAgentModalityClicked(props.nonAgentModality.Id, props.nonAgentModality.Link, props.alchemyTheme);
    if (props.isGetHelp && !props.isDarkTheme) {
      onOverrideClickBehavior(props.articleOnClickHandler)(event);
    }
  };

  return (
    <Stack styles={getModalitySectionBoxStyle(true)}>
      <Link
        styles={getModalityLinkStyle(props.theme.palette.themeDarkAlt)}
        href={props.nonAgentModality.Link}
        target="_blank"
        onClick={onClick}
        id={`NonAgent_Modality_${props.nonAgentModality.Id}`}
        data-automation-id={AutomationIds.NonAgentModalityRedirectURL}
        data-bi-bhvr='CONTACT'
        data-bi-area='modalitiesSection'
        data-bi-formnm='maxFunnel'
        data-bi-name={`NonAgent_Modality_${props.nonAgentModality.Id}`}
      >
        {props.nonAgentModality.Title}
      </Link>
      <StyledText styles={modalityDescriptionStyle}>
        {setDangerousInnerHtml(props.nonAgentModality.Description)}
      </StyledText>
    </Stack>
  );
};

export const NonAgentModalityStandard = (props: NonAgentModalityStandardProps) => {
    const contactUsClassNames = getContactUsClassNames(getContactUsStyles, { theme: props.theme });
    const linkStyles = getLinkStyles();
    const icon = (props.nonAgentModality.Id === C2CSkylightNonAgentModalityId) || (props.nonAgentModality.Id === C2CSkylightOfficeNonAgentModalityId) ? <FontIcon className={contactUsClassNames.requestModalityIcon} aria-label={'Phone'} iconName={'Phone'} />: null;
    return (
        (props.nonAgentModality.Type === NonAgentModalityType.Standard) &&
        <div className={mergeStyles('col-lg-12', contactUsClassNames.col, contactUsClassNames.bodyCopySmall, contactUsClassNames.modalityOption, contactUsClassNames.wrapText)}>
            <Link className={contactUsClassNames.modalityOptionTitle}
                href={props.nonAgentModality.Link}
                id={`NonAgent_Modality_${props.nonAgentModality.Id}`}
                styles={linkStyles}
                data-bi-bhvr='CONTACT'
                data-bi-area='modalitiesSection'
                data-bi-formnm='maxFunnel'
                data-bi-name={`NonAgent_Modality_${props.nonAgentModality.Id}`}
                target='_blank'>
                {icon}{props.nonAgentModality.Title}
            </Link>
            <div className={contactUsClassNames.descriptionHtmlText}>{setDangerousInnerHtml(props.nonAgentModality.Description)}</div>
        </div>
    );
};

export interface NonAgentModalityRedirectUrlProps {
    nonAgentModality: NonAgentModality;
    theme: IContactUsCurrentTheme;
    alchemyTheme?: string;
}

export const NonAgentModalityRedirectUrlBox = (props: NonAgentModalityRedirectUrlProps) => {
  if (props.nonAgentModality.Type !== NonAgentModalityType.RedirectURL) {
    return null;
  }

  const onClick = async (event?: React.MouseEvent) => {
    getHelpLogger.logNonAgentModalityClicked(props.nonAgentModality.Id, props.nonAgentModality.Link, props.alchemyTheme);
  };
  return (
    <Stack styles={getModalitySectionBoxStyle(true)}>
      <Link
        styles={getModalityLinkStyle(props.theme.palette.themeDarkAlt)}
        href={props.nonAgentModality.Link}
        target="_blank"
        onClick={onClick}
        id={`NonAgent_Modality_${props.nonAgentModality.Id}`}
        data-automation-id={AutomationIds.NonAgentModalityRedirectURL}
        data-bi-bhvr='CONTACT'
        data-bi-area='modalitiesSection'
        data-bi-formnm='maxFunnel'
        data-bi-name={`NonAgent_Modality_${props.nonAgentModality.Id}`}
      >
        {props.nonAgentModality.Title}
      </Link>
      <StyledText styles={modalityDescriptionStyle}>
        {setDangerousInnerHtml(props.nonAgentModality.Description)}
      </StyledText>
    </Stack>
  );
};

export const NonAgentModalityRedirectUrl = (props: NonAgentModalityRedirectUrlProps) => {
    const contactUsClassNames = getContactUsClassNames(getContactUsStyles, { theme: props.theme });
    const linkStyles = getLinkStyles();
    return (
        (props.nonAgentModality.Type === NonAgentModalityType.RedirectURL) &&
        <div className={mergeStyles('col-lg-12', contactUsClassNames.col, contactUsClassNames.bodyCopySmall, contactUsClassNames.modalityOption, contactUsClassNames.wrapText)}>
            <Link className={contactUsClassNames.modalityOptionTitle}
                href={props.nonAgentModality.Link}
                id={`NonAgent_Modality_${props.nonAgentModality.Id}`}
                styles={linkStyles}
                data-automation-id={AutomationIds.NonAgentModalityRedirectURL}
                data-bi-bhvr='CONTACT'
                data-bi-area='modalitiesSection'
                data-bi-formnm='maxFunnel'
                data-bi-name={`NonAgent_Modality_${props.nonAgentModality.Id}`}>
                {props.nonAgentModality.Title}
            </Link>
            <div className={contactUsClassNames.descriptionHtmlText}>{setDangerousInnerHtml(props.nonAgentModality.Description)}</div>
        </div>
    );
};

export interface NonAgentModalityPromotionProps {
    nonAgentModality: NonAgentModality;
    dispatchFetchAvailableModalitiesV2(): void;
    updateDraftRequest: (properties: Partial<FreelanceRequest>) => void;
    theme: IContactUsCurrentTheme;
}

export const NonAgentModalityPromotion = (props: NonAgentModalityPromotionProps) => {
    const handleUpsellTryAgainButtonClick = (e: any) => {
      getHelpLogger.logRefreshSupportActionsClicked(props.nonAgentModality.Link);
      e.preventDefault();
      e.stopPropagation();
      props.dispatchFetchAvailableModalitiesV2();
    };

    const handleSubscriptionButtonClick = (e: any) => {
      getHelpLogger.logPromoReviewPlanLinkClicked(props.nonAgentModality.Link);
      e.preventDefault();
      e.stopPropagation();
      switch (props.nonAgentModality.Id.toLowerCase()) {
          case M365UpsellNonAgentModalityId.m365Upsell:
              window.open(props.nonAgentModality.Link, '_blank');
              break;
          case M365UpsellNonAgentModalityId.agentAssistSubscription:
          case M365UpsellNonAgentModalityId.agentAssistSubscriptionChatWeighted:
          case M365UpsellNonAgentModalityId.payPerIncident:
          case M365UpsellNonAgentModalityId.payPerIncidentChatWeighted:
              const metaDataKey = props.nonAgentModality.Id.toLowerCase().indexOf(M365UpsellNonAgentModalityId.agentAssistSubscription) !== -1
                  ? MetaDataKeys.IsAgentAssistSubscriptionSignup
                  : MetaDataKeys.IsPayPerIncidentExperiment;
              props.updateDraftRequest({ MetaDatas: [{ Key: metaDataKey, Value: 'true' }] });
              props.dispatchFetchAvailableModalitiesV2();
              break;
      }
    };

    const isPayPerIncidentOrAgentAssistSub = props.nonAgentModality.Id.toLowerCase().indexOf(M365UpsellNonAgentModalityId.payPerIncident) !== -1 ||
        props.nonAgentModality.Id.toLowerCase().indexOf(M365UpsellNonAgentModalityId.agentAssistSubscription) !== -1;
    const contactUsClassNames = getContactUsClassNames(getContactUsStyles, { theme: props.theme });
    const linkStyles = getLinkStyles();
    const primaryUpsellButtonStyles = getUpsellButtonStyles({ theme: props.theme });
    const defaultUpsellButtonStyles = getUpsellButtonStyles({ theme: props.theme, isDefault: true });
    // last part of campaign id will be unique, use it for logging
    // link example: https://www.microsoft.com/en-us/microsoft-365/buy/compare-all-microsoft-365-products?ocid=PROD_Support_CONS_Amplify_UPS_Support_contactmodality1
    const upsellCampaignIdKey = props.nonAgentModality.Link ? props.nonAgentModality.Link.split('_').pop() : '';
    const icon = <FontIcon className={contactUsClassNames.requestModalityIcon} aria-label='Phone' iconName='Phone' />;
    return (
        <div>
            {(props.nonAgentModality.Type === NonAgentModalityType.Promotion) &&
                <div>
                    {
                        // Upsell Message Subscription
                        (props.nonAgentModality.Id.toLowerCase() === M365UpsellNonAgentModalityId.m365Upsell && !isGetHelp()) &&
                        <div className={mergeStyles('col-lg-12', contactUsClassNames.col, contactUsClassNames.upsellContainer)}>
                            <div className={contactUsClassNames.upsellTitle}>{props.nonAgentModality.Title}</div>
                            <div className={contactUsClassNames.upsellDescription}>{props.nonAgentModality.Description}</div>
                            <div className={contactUsClassNames.upsellSubdescription}>{getString('m365Upsell_smcSubDescriptionText')}</div>
                            <PrimaryButton
                                id='upsellSubscribeButton'
                                data-bi-bhvr='CONTACT'
                                data-bi-area='modalitiesSection'
                                data-bi-name={`upsellSubscribeButton_${upsellCampaignIdKey}`}
                                data-bi-formnm='maxFunnel'
                                onClick={handleSubscriptionButtonClick}
                                styles={primaryUpsellButtonStyles}
                                tabIndex={0}
                                text={getString('m365Upsell_smcSubscribeButtonText')}
                            />
                            <DefaultButton
                                id='upsellTryAgainButton'
                                data-bi-bhvr='CONTACT'
                                data-bi-area='modalitiesSection'
                                data-bi-name={`upsellTryAgainButton_${upsellCampaignIdKey}`}
                                data-bi-formnm='maxFunnel'
                                onClick={handleUpsellTryAgainButtonClick}
                                styles={defaultUpsellButtonStyles}
                                tabIndex={0}
                                text={getString('m365Upsell_smcTryAgainButtonText')}
                            />
                        </div>
                    }
                    {
                        // Upsell Message Subscription
                        (props.nonAgentModality.Id.toLowerCase() === M365UpsellNonAgentModalityId.m365Upsell && isGetHelp()) &&
                        <Stack styles={getModalitySectionBoxStyle()} horizontal tokens={{ childrenGap: 23 }}>
                          <Icon iconName="Phone" styles={getModalityIconStyle(props.theme.palette.themeDarkAlt)} />
                          <Stack tokens={{ childrenGap: 12 }}>
                            <Stack>
                              <StyledText styles={upSellTitleStyle}>{props.nonAgentModality.Title}</StyledText>
                              <StyledText>{props.nonAgentModality.Description}</StyledText>
                            </Stack>
                            <StyledText styles={getUpSellDescriptionStyle(props.theme.palette.neutralSecondary)}>
                              {getString('UpSellDescription')}
                            </StyledText>
                            <Stack>
                              <Stack horizontal tokens={{ childrenGap: 12 }}>
                                <PrimaryButton
                                  id='upsellSubscribeButton'
                                  data-bi-bhvr='CONTACT'
                                  data-bi-area='modalitiesSection'
                                  data-bi-name={`upsellSubscribeButton_${upsellCampaignIdKey}`}
                                  data-bi-formnm='maxFunnel'
                                  styles={primaryUpsellButtonStyles}
                                  text={getString('m365Upsell_smcSubscribeButtonText')}
                                  onClick={handleSubscriptionButtonClick}
                                />
                                <DefaultButton
                                  id='upsellTryAgainButton'
                                  data-bi-bhvr='CONTACT'
                                  data-bi-area='modalitiesSection'
                                  data-bi-name={`upsellTryAgainButton_${upsellCampaignIdKey}`}
                                  data-bi-formnm='maxFunnel'
                                  styles={defaultUpsellButtonStyles}
                                  text={getString('m365Upsell_smcTryAgainButtonText')}
                                  onClick={handleUpsellTryAgainButtonClick}
                                />
                              </Stack>
                            </Stack>
                          </Stack>
                        </Stack>
                    }
                    {
                        // Pay-Per-Call / Agent Assisted Subscription / Chat Weighted
                        // data-bi-name examples: M365UpsellSMCPayPerIncident_nocampaignlink , M365UpsellSMCAgentAssistSubscription_contactmodality5
                        (isPayPerIncidentOrAgentAssistSub) &&
                        <div className={mergeStyles('col-lg-12', contactUsClassNames.col)}>
                            <div className={contactUsClassNames.payOptionHeader}>{getString('m365Upsell_payOptionHeader')}</div>
                            <div className={mergeStyles(contactUsClassNames.bodyCopySmall, contactUsClassNames.modalityOption, contactUsClassNames.upsellScenario, contactUsClassNames.wrapText)}>
                                <Link className={contactUsClassNames.modalityOptionTitle}
                                    id='upsellModalityOption'
                                    styles={linkStyles}
                                    tabIndex={0}
                                    data-bi-bhvr='CONTACT'
                                    data-bi-area='modalitiesSection'
                                    data-bi-name={`${props.nonAgentModality.Id.replace(/\s/g, '')}_${upsellCampaignIdKey}}}`}
                                    data-bi-formnm='maxFunnel'
                                    onClick={handleSubscriptionButtonClick}>
                                    <span className='upsell-icon'>{icon}</span>{props.nonAgentModality.Title}
                                </Link>
                                <div className={contactUsClassNames.payOptionDescription} >
                                    {props.nonAgentModality.Description}
                                </div>
                            </div>
                        </div>
                    }
                </div>
            }
        </div>
    );
};

export interface NonAgentModalityDescriptionOnlyProps {
    nonAgentModality: NonAgentModality;
    theme: IContactUsCurrentTheme;
    isGetHelp?: boolean;
    isDarkTheme?: boolean;
  articleOnClickHandler?: (url: string) => {};
    alchemyTheme?: string;
}

export const NonAgentModalityDescriptionOnlyBox = (props: NonAgentModalityDescriptionOnlyProps) => {
  if (props.nonAgentModality.Type !== NonAgentModalityType.DescriptionOnly) {
    return null;
  }
  const onClick = async (event?: React.MouseEvent) => {
    getHelpLogger.logNonAgentModalityClicked(props.nonAgentModality.Id, props.nonAgentModality.Link, props.alchemyTheme);
    if (props.isGetHelp && !props.isDarkTheme) {
      onOverrideClickBehavior(props.articleOnClickHandler)(event);
    }
  };

  const content = props.nonAgentModality?.Description?.split('{0}');

  return !!content && content.length >= 2 ? (
    <Stack styles={getModalitySectionBoxStyle(true)}>
      <StyledText styles={modalityDescriptionStyle}>
        {content[0]}
        <Link
          styles={getModalityLinkStyle(props.theme.palette.themeDarkAlt)}
          href={props.nonAgentModality.Link}
          target="_blank"
          onClick={onClick}
          id={`NonAgent_Modality_${props.nonAgentModality.Id}`}
          data-bi-bhvr='CONTACT'
          data-bi-area='modalitiesSection'
          data-bi-formnm='maxFunnel'
          data-bi-name={`NonAgent_Modality_${props.nonAgentModality.Id}`}
        >
          {props.nonAgentModality.Title}
        </Link>
        {content[1]}
      </StyledText>
    </Stack>
  ) : null;
};

export const NonAgentModalityDescriptionOnly = (props: NonAgentModalityDescriptionOnlyProps) => {
    const contactUsClassNames = getContactUsClassNames(getContactUsStyles, { theme: props.theme });
    return (
        (props.nonAgentModality.Type === NonAgentModalityType.DescriptionOnly) &&
        <div className={mergeStyles('col-lg-12', contactUsClassNames.col, contactUsClassNames.bodyCopySmall, contactUsClassNames.modalityOption, contactUsClassNames.wrapText)}>
            <div className={contactUsClassNames.descriptionHtmlText}>{setDangerousInnerHtml(props.nonAgentModality.Description.replace('{0}',
                `<a
                    href=${props.nonAgentModality.Link}
                    id=${`NonAgent_Modality_${props.nonAgentModality.Id}`}
                    data-bi-bhvr='CONTACT'
                    data-bi-area='modalitiesSection'
                    data-bi-formnm='maxFunnel'
                    data-bi-name=${`NonAgent_Modality_${props.nonAgentModality.Id}`}
                    target='_blank'>
                    ${props.nonAgentModality.Title}</a>`))}
            </div>
        </div>
    );
};

export interface NonAgentModalityAMCAskCommunityProps {
  nonAgentModality: NonAgentModality;
  theme: IContactUsCurrentTheme;
  alchemyTheme?: string;
}

export const NonAgentModalityAMCAskCommunity = (props: NonAgentModalityAMCAskCommunityProps) => {
  const contactUsClassNames = getContactUsClassNames(getContactUsStyles, { theme: props.theme });
  const linkStyles = getLinkStyles();
  const AMCicon = <FontIcon className={contactUsClassNames.requestModalityIcon} aria-label={'Group'} iconName={'Group'} />;

  const AskCommunityLink = isGetHelp() ? props.nonAgentModality.Link.replace('ContactUsWebModality', 'GetHelpDeflect') : props.nonAgentModality.Link;

  // Splitting the string due to there being possible personal user information in the query parameters.
  const onClick = async (event?: React.MouseEvent) => {
    const regex = /login_hint=.*?(&|$)/;
    const loggedUrl = AskCommunityLink.replace(regex, '');
    getHelpLogger.logNonAgentModalityClicked(props.nonAgentModality.Id, loggedUrl, props.alchemyTheme);
  };

  return (
      (props.nonAgentModality.Type === NonAgentModalityType.AMCAskCommunity) &&
      <div className={mergeStyles('col-lg-12', contactUsClassNames.col, contactUsClassNames.bodyCopySmall, contactUsClassNames.modalityOption, contactUsClassNames.wrapText)}>
          <Link
            className={contactUsClassNames.modalityOptionTitle}
            onClick={onClick}
            id={`NonAgent_Modality_${props.nonAgentModality.Id}`}
            styles={linkStyles}
            data-bi-bhvr='CONTACT'
            data-bi-area='modalitiesSection'
            data-bi-formnm='maxFunnel'
            data-bi-name={`NonAgent_Modality_${props.nonAgentModality.Id}`}
            href={AskCommunityLink}
            target='_blank'
        >{AMCicon}{props.nonAgentModality.Title}</Link>
          <div className={contactUsClassNames.descriptionHtmlText}>{setDangerousInnerHtml(props.nonAgentModality.Description)}</div>
      </div>
  );
};

export const AskCommunityBox = (props: IThemedProp) => (
  <Stack styles={getModalitySectionBoxStyle()} horizontal tokens={{ childrenGap: 23 }}>
    <Icon iconName="Group" styles={getModalityIconStyle(props.theme.palette.themeDarkAlt)} />
    <Stack>
      <Link styles={getModalityLinkStyle(props.theme.palette.themeDarkAlt)} href={SMCTicketing.CommunityUrl} target="_blank">
        {getString('GetHelp_ContactSupport_AskCommunity')}
      </Link>
      <StyledText>{getString('GetHelp_ContactSupport_AskCommunityDescription')}</StyledText>
    </Stack>
  </Stack>
);
